import React from 'react'
import { i18n } from '../../index'
import { Mutation } from 'react-apollo'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import omit from 'lodash/omit'
import {
  createShiftResponsibilityMutation,
  updateShiftResponsibilityMutation,
  fetchChainShiftResponsibilities,
  fetchStationShiftResponsibilities,
} from '../../shiftResponsibility/shiftResponsibilityQueries'
import type { ShiftResponsibility } from '../../shiftResponsibility/shiftResponsibilityTypes'
import css from '../incentiveProduct/incentiveProductForm.css'
const defaultFormData = {
  id: null,
  name: '',
}

const updateCacheOnUpdate =
  (query: any, fieldName: string, stationId: string | null | undefined) =>
  (cache: any, { data: { updateShiftResponsibility } }: any) => {
    const variables = stationId
      ? {
          stationId,
        }
      : {}
    const data = cache.readQuery({
      query,
      variables,
    })
    cache.writeQuery({
      query,
      variables,
      data: {
        [fieldName]: data[fieldName].map((ts) => {
          return ts.id === updateShiftResponsibility.id ? updateShiftResponsibility : ts
        }),
      },
    })
  }

const updateCacheOnCreate =
  (query: any, fieldName: string, stationId: string | null | undefined) =>
  (cache: any, { data: { createShiftResponsibility } }: any) => {
    const variables = stationId
      ? {
          stationId,
        }
      : {}
    const data = cache.readQuery({
      query,
      variables,
    })
    cache.writeQuery({
      query,
      variables,
      data: {
        [fieldName]: [...data[fieldName], createShiftResponsibility],
      },
    })
  }

type Props = {
  data: ShiftResponsibility | null | undefined
  stationId?: string
  onSubmit: (...args: Array<any>) => any
  onCancel: (...args: Array<any>) => any
}
type FormData = {
  id: string | null | undefined
  name: string
}
type State = {
  formData: FormData
}

class ShiftResponsibilityForm extends React.Component<Props, State> {
  state = {
    formData: { ...defaultFormData },
  }

  componentDidMount() {
    const { data } = this.props

    if (data) {
      this.setStateFromData(data)
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { data } = this.props

    if (data && JSON.stringify(data) !== JSON.stringify(prevProps.data)) {
      this.setStateFromData(data)
    }
  }

  setStateFromData = (data: ShiftResponsibility) => {
    this.setState({
      formData: { ...defaultFormData, id: data.id, name: data.name },
    })
  }
  onInput = (fieldName: string, newValue: (string | null | undefined) | number) => {
    this.setState({
      formData: { ...this.state.formData, [fieldName]: newValue },
    })
  }
  onSubmit = (onMutate: (...args: Array<any>) => any, e: any) => {
    e.preventDefault()
    const { stationId } = this.props
    const formData = {
      ...this.state.formData,
      id: this.state.formData.id,
      name: this.state.formData.name,
    }
    const query = stationId ? fetchStationShiftResponsibilities : fetchChainShiftResponsibilities
    const fieldName = stationId ? 'stationShiftResponsibilities' : 'chainShiftResponsibilities'
    onMutate(
      formData.id
        ? {
            variables: { ...formData },
            update: updateCacheOnUpdate(query, fieldName, stationId),
          }
        : {
            variables: stationId ? { ...omit(formData, ['id']), stationId } : { ...omit(formData, ['id']) },
            update: updateCacheOnCreate(query, fieldName, stationId),
          },
    )
    this.props.onSubmit()
    this.onReset()
  }
  onReset = () => {
    this.setState({
      formData: { ...defaultFormData },
    })
  }

  render() {
    const { onCancel } = this.props
    const { formData } = this.state
    return (
      <Mutation mutation={formData.id ? updateShiftResponsibilityMutation : createShiftResponsibilityMutation}>
        {(onMutate) => (
          <form onSubmit={(e) => this.onSubmit(onMutate, e)} className={css.component}>
            <TextField
              autoFocus
              required
              label={i18n.t('admin.shiftResponsibilities.label.name')}
              value={formData.name}
              onChange={(e) => this.onInput('name', e.target.value)}
              className={css.textField}
            />
            <DialogActions className={css.actions}>
              <Button type='button' onClick={onCancel}>
                {i18n.t('buttons.cancel')}
              </Button>
              <Button type='submit' color='primary'>
                {i18n.t('buttons.save')}
              </Button>
            </DialogActions>
          </form>
        )}
      </Mutation>
    )
  }
}

export default ShiftResponsibilityForm
